import { Descriptions, Row, Col, Skeleton, Select, Space, Typography, InputNumber } from "antd"
import { useCollection } from "../../../hooks/useCollection"
import KnifePopup from "./Knife/KnifePopup"
import { useState } from "react"

const { Text } = Typography

export default function OrderProductDetails({ orderData, setOrderData, loading }) {
  const { data: machineData, loading: machineLoading } = useCollection("machines")

  // Ensure machineData is not null before filtering
  const filteredMachineData = machineData
    ? machineData.filter((machine) => machine.machine !== "Cutter")
    : []

  //print settings data
  const { data: varnishData, loading: varnishLoading } = useCollection("varnish")
  const { data: reliefData, loading: reliefLoading } = useCollection("relief")
  const { data: foilData, loading: foilLoading } = useCollection("foil")

  //if knife gets updated, displays that
  const [updatedKnife, setUpdatedKnife] = useState(false)

  const data = orderData

  if (!data) {
    return null // or render an error message
  }

  const product = data.selectedProduct
  const print = product.print

  //handle print change
  const handlePrintChange = (key, value) => {
    setOrderData((prevOrder) => ({
      ...prevOrder,
      selectedProduct: {
        ...prevOrder.selectedProduct,
        print: {
          ...prevOrder.selectedProduct.print,
          [key]: value,
        },
      },
    }))
  }

  //checks if knife has been updated, displays message
  const wasKnifeUpdated = (text) => {
    if (updatedKnife) {
      return (
        <>
          <Space>
            <Text type="danger" italic>
              {text}
            </Text>
            <Text italic type="danger" code>
              changed
            </Text>
          </Space>
        </>
      )
    } else {
      return <>{text}</>
    }
  }

  const handleMachine = (value, options) => {
    setOrderData((prevOrderData) => {
      const newOrderData = {
        ...prevOrderData,
        selectedProduct: {
          ...prevOrderData.selectedProduct,
          machine: value,
          machineCosts: options.costs,
        },
      }
      return newOrderData
    })
  }

  const selectMachine = () => {
    const selectedMachine = orderData.selectedProduct.machine // Get the selected machine value from orderData
    return (
      <Select
        size="small"
        showSearch
        fieldNames={{ value: "machine" }}
        options={filteredMachineData} // Use the filtered machineData
        onChange={(value, options) => handleMachine(value, options)}
        loading={machineLoading}
        style={{ width: "90%" }}
        defaultValue={selectedMachine} // Set defaultValue based on selected machine value
      />
    )
  }

  //handle new knife box
  const handleNewKnife = (value) => {
    setOrderData((prevOrder) => ({
      ...prevOrder,
      hasNewKnife: value,
    }))
  }

  const productDetails = () => {
    return (
      <Descriptions size="small" bordered title="Product Details">
        <Descriptions.Item label="Knife">
          <KnifePopup
            knifeId={product.knifeId}
            setUpdatedKnife={setUpdatedKnife}
            setOrderData={setOrderData}
            selectedMachine={product.machine}
            knifeCode={product.knifeCode}
          />
        </Descriptions.Item>
        <Descriptions.Item label="New Knife?">
          <Select
            options={[
              {
                label: "yes",
                value: true,
              },
              {
                label: "no",
                value: false,
              },
            ]}
            onChange={handleNewKnife}
            style={{ width: "100%" }}
            size="small"
            defaultValue={data.hasNewKnife}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Width">{wasKnifeUpdated(product.labelWidth)}</Descriptions.Item>
        <Descriptions.Item label="Height">{wasKnifeUpdated(product.labelHeight)}</Descriptions.Item>
        <Descriptions.Item label="Machine">{selectMachine()}</Descriptions.Item>
        <Descriptions.Item label="Material">{product.material}</Descriptions.Item>
        <Descriptions.Item label="Format Width">{product.formatWidth}</Descriptions.Item>
        <Descriptions.Item label="Columns">
          {wasKnifeUpdated(product.numberOfColumns)}
        </Descriptions.Item>
        <Descriptions.Item label="Label Count">{data.labelCount}</Descriptions.Item>
        {product.advanced.changableSpace && product.advanced.changableSpace ? (
          <Descriptions.Item contentStyle={{ color: "red" }} label="Changable Space">
            {data.changableSpaceData}
          </Descriptions.Item>
        ) : (
          ""
        )}
      </Descriptions>
    )
  }

  const printDetails = () => {
    return (
      <Descriptions column={1} size="small" title="Print">
        <Descriptions.Item contentStyle={{ color: "#1677ff" }} label="CMYK Colors">
          <Space.Compact size="small">
            <InputNumber
              value={print.cmykColors}
              style={{ width: "30%" }}
              onChange={(value) => handlePrintChange("cmykColors", value)}
            />
            <InputNumber
              addonAfter="%"
              value={print.cmykPercent * 100}
              style={{ width: "80%" }}
              onChange={(value) => handlePrintChange("cmykPercent", value / 100)}
              max={100}
            />
          </Space.Compact>
        </Descriptions.Item>

        <Descriptions.Item contentStyle={{ color: "#1677ff" }} label="Pantone">
          <Space.Compact size="small">
            <InputNumber
              value={print.pantoneColors}
              style={{ width: "30%" }}
              onChange={(value) => handlePrintChange("pantoneColors", value)}
            />
            <InputNumber
              addonAfter="%"
              value={print.pantonePercent * 100}
              style={{ width: "80%" }}
              onChange={(value) => handlePrintChange("pantonePercent", value / 100)}
              max={100}
            />
          </Space.Compact>
        </Descriptions.Item>

        <Descriptions.Item contentStyle={{ color: "#1677ff" }} label="Covering White">
          <Space.Compact size="small">
            <InputNumber
              value={print.coveringWCount}
              style={{ width: "30%" }}
              onChange={(value) => handlePrintChange("coveringWCount", value)}
            />
            <InputNumber
              addonAfter="%"
              value={print.coveringWPercent * 100}
              style={{ width: "80%" }}
              onChange={(value) => handlePrintChange("coveringWPercent", value / 100)}
              max={100}
            />
          </Space.Compact>
        </Descriptions.Item>

        <Descriptions.Item contentStyle={{ color: "#1677ff" }} label="Silver / Gold">
          <Space.Compact size="small">
            <InputNumber
              value={print.goldOrSilver}
              style={{ width: "30%" }}
              onChange={(value) => handlePrintChange("goldOrSilver", value)}
            />
            <InputNumber
              addonAfter="%"
              value={print.goldOrSilverPercent * 100}
              style={{ width: "80%" }}
              onChange={(value) => handlePrintChange("goldOrSilverPercent", value / 100)}
              max={100}
            />
          </Space.Compact>
        </Descriptions.Item>

        <Descriptions.Item contentStyle={{ color: "#1677ff" }} label="Varnish">
          <Space.Compact size="small">
            <Select
              loading={varnishLoading}
              options={varnishData}
              fieldNames={{ label: "type", value: "type" }}
              defaultValue={print.varnishType}
              style={{ width: "50%" }}
              onChange={(value) => handlePrintChange("varnishType", value / 100)}
            />
            <InputNumber
              addonAfter="%"
              value={print.varnishPercent * 100}
              style={{ width: "50%" }}
              onChange={(value) => handlePrintChange("varnishPercent", value / 100)}
              max={100}
            />
          </Space.Compact>
        </Descriptions.Item>

        <Descriptions.Item contentStyle={{ color: "#1677ff" }} label="Relief">
          <Select
            size="small"
            loading={reliefLoading}
            options={reliefData}
            fieldNames={{ label: "tag", value: "tag" }}
            defaultValue={print.relief === "Yes" ? "Yes" : "No"}
            style={{ width: "50%" }}
            onChange={(value) => handlePrintChange("relief", value)}
          />
        </Descriptions.Item>

        <Descriptions.Item contentStyle={{ color: "#1677ff" }} label="Foil">
          <Space.Compact size="small">
            <InputNumber
              value={print.foilCount}
              style={{ width: "30%" }}
              onChange={(value) => handlePrintChange("foilCount", value)}
            />
            <Select
              size="small"
              loading={foilLoading}
              options={foilData}
              fieldNames={{ label: "color", value: "color" }}
              defaultValue={print.foilColor}
              style={{ width: "80%" }}
              onChange={(value) => handlePrintChange("foilColor", value)}
            />
            <InputNumber
              value={print.foilPercent * 100}
              style={{ width: "80%" }}
              onChange={(value) => handlePrintChange("foilPercent", value / 100)}
              max={100}
            />
          </Space.Compact>
        </Descriptions.Item>
      </Descriptions>
    )
  }

  if (loading) {
    return (
      <>
        <Skeleton active />;
      </>
    )
  }

  return (
    <div style={{ background: "white", padding: 15, borderRadius: 10 }}>
      <Row gutter={[15, 10]}>
        <Col span={16}>{productDetails()}</Col>
        <Col span={8}>{printDetails()}</Col>
      </Row>
    </div>
  )
}
